import { Building, type LucideIcon, MapPin, Users } from "lucide-react";

export type SettingsSection = "organization";

const ORGANIZATION_SETTINGS_TABS = ["brand", "team", "stores"] as const;

export type OrganizationSettingsTab =
  (typeof ORGANIZATION_SETTINGS_TABS)[number];

export type SettingsTab = OrganizationSettingsTab;

export const SETTINGS_ENTRY_LABELS = {
  profile: "Account settings",
  organization: "Org settings",
} as const;

type SettingsNavItem = {
  tab: SettingsTab;
  label: string;
  tabLabel: string;
  title: string;
  description: string;
  icon: LucideIcon;
};

export type FlatSettingsNavItem = SettingsNavItem & {
  section: SettingsSection;
};

const ORGANIZATION_NAV: SettingsNavItem[] = [
  {
    tab: "brand",
    label: "Organization profile",
    tabLabel: "General",
    title: "Organization profile",
    description: "Logo, name, and industry",
    icon: Building,
  },
  {
    tab: "team",
    label: "Team & invites",
    tabLabel: "Team",
    title: "Team & invites",
    description: "Members and pending invitations",
    icon: Users,
  },
  {
    tab: "stores",
    label: "Stores",
    tabLabel: "Stores",
    title: "Stores",
    description: "Physical store locations for this brand",
    icon: MapPin,
  },
];

function getOrganizationNavItems() {
  return ORGANIZATION_NAV;
}

export function getFlatSettingsNavItems(
  userType: "employee" | "employer" | undefined
): FlatSettingsNavItem[] {
  if (userType !== "employer") {
    return [];
  }

  return getOrganizationNavItems().map((item) => ({
    ...item,
    section: "organization" as const,
  }));
}

export function getSettingsNavItem(tab: string): SettingsNavItem | undefined {
  return ORGANIZATION_NAV.find((item) => item.tab === tab);
}

function isOrganizationSettingsTab(
  tab: string
): tab is OrganizationSettingsTab {
  return (ORGANIZATION_SETTINGS_TABS as readonly string[]).includes(tab);
}

function defaultSettingsTab(
  _userType: "employee" | "employer" | undefined
): SettingsTab {
  return "brand";
}

function isTabAllowedForUser(
  _tab: SettingsTab,
  userType: "employee" | "employer" | undefined
): boolean {
  return userType === "employer";
}

type SettingsPathParams = {
  tab: SettingsTab;
  query?: Record<string, string>;
};

export function settingsPath({ tab, query }: SettingsPathParams): string {
  const params = new URLSearchParams({ tab });
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      params.set(key, value);
    }
  }
  return `/settings?${params.toString()}`;
}

export function settingsPathForEmployerDefault(): string {
  return settingsPath({ tab: "brand" });
}

function isValidSettingsTab(tab: string): tab is SettingsTab {
  return isOrganizationSettingsTab(tab);
}

export function resolveSettingsLocation({
  tabParam,
  userType,
}: {
  tabParam: string | null;
  userType: "employee" | "employer" | undefined;
}): { section: SettingsSection; tab: SettingsTab } {
  const defaultTab = defaultSettingsTab(userType);

  if (!(tabParam && isValidSettingsTab(tabParam))) {
    return { section: "organization", tab: defaultTab };
  }

  if (!isTabAllowedForUser(tabParam, userType)) {
    return { section: "organization", tab: defaultTab };
  }

  return { section: "organization", tab: tabParam };
}

export function canonicalSettingsSearchParams({
  tabParam,
  userType,
  searchParams,
}: {
  tabParam: string | null;
  userType: "employee" | "employer" | undefined;
  searchParams: URLSearchParams;
}): URLSearchParams {
  const { tab } = resolveSettingsLocation({ tabParam, userType });
  const params = new URLSearchParams();

  params.set("tab", tab);

  for (const [key, value] of searchParams.entries()) {
    if (key === "section" || key === "tab") {
      continue;
    }
    params.set(key, value);
  }

  return params;
}
